import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PRIVACY_SECTIONS = [
  {
    h: "1. Overview",
    blocks: [
      { type: "p", text: "VantStride (“VantStride”, “we”, “us”, or “our”) is a finance-career education and resource platform. This Privacy Policy explains how information may be collected, used, stored, and shared when you access or use VantStride, create an account, interact with VantStride AI, access Career Hubs, browse learning resources, purchase or access digital products, follow third-party or affiliate links, communicate with us, or otherwise use our platform." },
      { type: "p", text: "By using VantStride, you acknowledge the practices described in this Privacy Policy." },
    ],
  },
  {
    h: "2. Information we collect",
    blocks: [
      { type: "p", text: "Depending on how you use VantStride, we may collect:" },
      { type: "h3", text: "Account Information" },
      { type: "p", text: "When you create an account, we may collect information such as your name, email address, authentication information, and information associated with your VantStride account." },
      { type: "h3", text: "AI Interaction Data" },
      { type: "p", text: "When you use VantStride AI, we may process the questions, prompts, messages, and other content you submit, together with information necessary to provide and maintain the AI experience. Conversation history may be associated with your account where the platform provides conversation-history functionality." },
      { type: "h3", text: "Usage and Technical Information" },
      { type: "p", text: "We may collect information about how the platform is used, including pages or Career Hubs viewed, features used, links clicked, interactions with resources, device or browser information, approximate technical information such as IP address where provided by our infrastructure, timestamps, and similar diagnostic or analytics information." },
      { type: "h3", text: "Communications" },
      { type: "p", text: "If you contact VantStride, join a mailing list, request information, or otherwise communicate with us, we may collect your name, email address, message, and other information you voluntarily provide." },
      { type: "h3", text: "Transaction-Related Information" },
      { type: "p", text: "When you purchase a VantStride digital product through an external payment or commerce platform, the transaction may be processed by that third-party provider. VantStride may receive limited transaction information necessary to identify or support a purchase, but payment-card or banking information entered directly with a third-party payment provider is handled according to that provider’s own privacy practices." },
    ],
  },
  {
    h: "3. How we use information",
    blocks: [
      { type: "p", text: "We may use information to:" },
      { type: "ul", items: [
        "create, authenticate and maintain user accounts;",
        "provide Career Hubs, VantStride AI and other platform functionality;",
        "maintain AI conversation history where this feature is available;",
        "provide requested resources and digital products;",
        "operate, maintain, troubleshoot and improve VantStride;",
        "understand how users interact with the platform;",
        "measure resource, Career Hub, product and affiliate-link engagement;",
        "respond to questions and support requests;",
        "communicate important account, platform or service information;",
        "send marketing or educational communications where permitted and where the user has chosen to receive them;",
        "prevent misuse, fraud, abuse or security incidents;",
        "comply with applicable legal obligations; and",
        "protect VantStride, its users and its services.",
      ]},
      { type: "p", text: "We do not sell personal information in exchange for money." },
    ],
  },
  {
    h: "4. VantStride AI",
    blocks: [
      { type: "p", text: "VantStride includes an AI-powered finance-career assistant." },
      { type: "p", text: "Information submitted to VantStride AI may be processed by technology and AI service providers used to generate responses and operate the feature." },
      { type: "p", text: "Users should avoid submitting passwords, payment-card details, government identification numbers, confidential employer information, or other highly sensitive personal information through VantStride AI." },
      { type: "p", text: "AI-generated responses may contain errors or inaccuracies and should not be treated as professional financial, investment, legal, tax, employment, or other professional advice." },
      { type: "p", text: "Where AI conversation history is available, conversations may be stored so users can access or continue previous conversations." },
    ],
  },
  {
    h: "5. Analytics and platform activity",
    blocks: [
      { type: "p", text: "VantStride may use analytics and internal event tracking to understand platform usage and improve the user experience." },
      { type: "p", text: "This may include events such as account creation, login activity, Career Hub views, resource interactions, product interactions, affiliate-link clicks, and other feature usage." },
      { type: "p", text: "Analytics information may be aggregated or otherwise processed to understand how VantStride is being used." },
    ],
  },
  {
    h: "6. Cookies and similar technologies",
    blocks: [
      { type: "p", text: "VantStride and service providers supporting the platform may use cookies, local storage, session technologies, or similar technologies where necessary for authentication, security, preferences, analytics, and platform functionality." },
      { type: "p", text: "The technologies used may change as VantStride develops. Where legally required, appropriate choices or consent mechanisms may be provided." },
    ],
  },
  {
    h: "7. Payments and digital products",
    blocks: [
      { type: "p", text: "VantStride may offer digital products through third-party commerce or payment platforms." },
      { type: "p", text: "When a user follows a purchase link, the transaction may occur on a third-party website. Information entered directly on that website is governed by that provider’s privacy policy and terms." },
      { type: "p", text: "VantStride should not be understood to directly collect or store complete payment-card information merely because a purchase originates from the VantStride platform." },
    ],
  },
  {
    h: "8. Affiliate links and third-party websites",
    blocks: [
      { type: "p", text: "VantStride contains curated recommendations and may contain affiliate links to third-party products, courses, platforms, communities, tools, books, or other resources." },
      { type: "p", text: "When you click an external or affiliate link, you may leave VantStride. The destination website may independently collect information about you according to its own privacy policy." },
      { type: "p", text: "VantStride is not responsible for the privacy practices, security, content, or data-processing practices of third-party websites or services." },
      { type: "p", text: "Some affiliate links may use tracking parameters or similar mechanisms that allow the relevant partner to attribute a referral or purchase to VantStride." },
      { type: "p", text: "For additional information regarding commercial relationships, users should review VantStride’s Affiliate Disclosure." },
    ],
  },
  {
    h: "9. Service providers",
    blocks: [
      { type: "p", text: "We may use third-party service providers to operate parts of VantStride, including services related to hosting, infrastructure, authentication, databases, analytics, artificial intelligence, email delivery, security, and payment or digital-product processing." },
      { type: "p", text: "These providers may process information as necessary to perform their services." },
      { type: "p", text: "Each third-party provider may also maintain its own privacy terms and practices." },
    ],
  },
  {
    h: "10. When information may be shared",
    blocks: [
      { type: "p", text: "We may disclose information:" },
      { type: "ul", items: [
        "to service providers that help operate VantStride;",
        "when necessary to provide functionality requested by a user;",
        "when required by applicable law, regulation, legal process, or governmental request;",
        "when reasonably necessary to investigate fraud, abuse, security threats, or violations of our terms;",
        "to protect the rights, property, security, or safety of VantStride, our users, or others; or",
        "in connection with a merger, acquisition, restructuring, financing, sale, or transfer of all or part of the platform or its assets, subject to applicable law.",
      ]},
      { type: "p", text: "We do not disclose personal information to third parties merely for unrelated purposes without an appropriate basis." },
    ],
  },
  {
    h: "11. Data retention",
    blocks: [
      { type: "p", text: "We retain information for as long as reasonably necessary for the purposes described in this Privacy Policy, including providing the platform, maintaining accounts and user-requested functionality, resolving disputes, maintaining security, and complying with applicable legal obligations." },
      { type: "p", text: "Retention periods may vary depending on the type of information and the reason it was collected." },
      { type: "p", text: "Where appropriate and technically available, information may be deleted or anonymized when it is no longer required." },
    ],
  },
  {
    h: "12. Data security",
    blocks: [
      { type: "p", text: "We take reasonable technical and organizational measures designed to protect information handled through VantStride." },
      { type: "p", text: "However, no website, database, internet transmission, or electronic storage system can be guaranteed to be completely secure. Users should therefore use strong passwords, keep login credentials confidential, and exercise appropriate care when submitting information online." },
    ],
  },
  {
    h: "13. Your choices and rights",
    blocks: [
      { type: "p", text: "Depending on your location and applicable law, you may have rights relating to your personal information, which may include requesting access, correction, updating, or deletion of certain personal information." },
      { type: "p", text: "You may also be able to manage certain information directly through your VantStride account." },
      { type: "p", text: "Where marketing emails are sent, you may unsubscribe using the unsubscribe mechanism provided in those communications." },
      { type: "p", text: "Requests concerning personal information may be submitted using the contact information below. We may need to verify a request before acting on it." },
    ],
  },
  {
    h: "14. International users",
    blocks: [
      { type: "p", text: "VantStride may be accessible to users in different countries. As a result, information may be processed or stored in countries other than the country in which a user is located, including through third-party technology providers." },
      { type: "p", text: "Where applicable, we take reasonable steps to use service providers and data-processing practices appropriate to the operation of the platform." },
    ],
  },
  {
    h: "15. Children’s privacy",
    blocks: [
      { type: "p", text: "VantStride is designed as a finance-career and educational platform and is not intended to knowingly collect personal information from children where parental or guardian consent would be legally required." },
      { type: "p", text: "If you believe a child has provided personal information to VantStride in circumstances where such consent was required, please contact us so the matter can be reviewed." },
    ],
  },
  {
    h: "16. Third-party content",
    blocks: [
      { type: "p", text: "References to or links to third-party websites, courses, communities, tools, resources, products, or services do not mean that VantStride controls those third parties’ privacy practices." },
      { type: "p", text: "Users should review the privacy policies and terms of any third-party service they choose to use." },
    ],
  },
  {
    h: "17. Changes to this Privacy Policy",
    blocks: [
      { type: "p", text: "We may update this Privacy Policy as VantStride evolves, including when we introduce new features, providers, products, or legal requirements." },
      { type: "p", text: "When material changes are made, the “Last updated” date on this page will be revised. Where required by applicable law, additional notice or consent may be provided." },
    ],
  },
  {
    h: "18. Contact",
    blocks: [
      { type: "p", text: "For privacy-related questions, requests, or concerns, contact:" },
      { type: "p", text: "VantStride" },
      { type: "mailto", email: "support@vantstride.com" },
    ],
  },
];

const TERMS_SECTIONS = [
  {
    h: "1. Acceptance of terms",
    blocks: [
      { type: "p", text: "Welcome to VantStride." },
      { type: "p", text: "These Terms & Conditions (“Terms”) govern your access to and use of the VantStride website, platform, Career Hubs, VantStride AI, ebooks, playbooks, digital resources, recommendations and related features or services (collectively, the “Services”)." },
      { type: "p", text: "By accessing or using VantStride, creating an account, or using any of our Services, you agree to these Terms and our Privacy Policy." },
      { type: "p", text: "If you do not agree to these Terms, please do not use the Services." },
    ],
  },
  {
    h: "2. About VantStride",
    blocks: [
      { type: "p", text: "VantStride is a finance-career education and resource platform designed to help users understand finance careers, prepare for them, discover learning resources and access educational materials." },
      { type: "p", text: "VantStride may provide Career Hubs, digital playbooks, ebooks, educational resources, curated third-party recommendations, affiliate links and AI-powered career information." },
      { type: "p", text: "VantStride is an educational platform and does not operate as an investment adviser, financial adviser, recruitment agency, university, employer or professional certification body unless expressly stated otherwise." },
    ],
  },
  {
    h: "3. Educational purpose",
    blocks: [
      { type: "p", text: "All information and materials provided through VantStride are intended for general educational and informational purposes." },
      { type: "p", text: "Nothing on VantStride constitutes financial, investment, legal, tax, accounting or other regulated professional advice." },
      { type: "p", text: "Users are responsible for independently evaluating information and making their own career, education, financial and professional decisions." },
    ],
  },
  {
    h: "4. No guarantee of career outcomes",
    blocks: [
      { type: "p", text: "VantStride does not guarantee employment, internships, interviews, admission to educational institutions, compensation levels, promotions, investment performance, career advancement or any other particular outcome." },
      { type: "p", text: "Career outcomes depend on many factors outside VantStride’s control, including a user's qualifications, skills, experience, location, market conditions, application quality, interview performance and employer decisions." },
      { type: "p", text: "Examples, career paths, compensation information, hiring information or other outcomes discussed on the platform are informational and should not be interpreted as promises or guarantees." },
    ],
  },
  {
    h: "5. User accounts",
    blocks: [
      { type: "p", text: "Certain VantStride features may require users to create an account." },
      { type: "p", text: "Users are responsible for:" },
      { type: "ul", items: [
        "providing accurate information when creating an account;",
        "maintaining the confidentiality of their login credentials;",
        "using a secure password;",
        "keeping account information reasonably current; and",
        "activity occurring through their account where attributable to their use or failure to protect their credentials.",
      ]},
      { type: "p", text: "Users must not impersonate another person, create accounts for fraudulent purposes, attempt to gain unauthorized access to another account, or interfere with VantStride’s authentication or security systems." },
      { type: "p", text: "If you believe your account has been compromised, contact VantStride promptly." },
    ],
  },
  {
    h: "6. VantStride AI",
    blocks: [
      { type: "p", text: "VantStride may provide an AI-powered finance-career assistant known as VantStride AI." },
      { type: "p", text: "AI-generated information may be incomplete, outdated, incorrect or unsuitable for a particular user's circumstances." },
      { type: "p", text: "Users should independently verify important information before relying on it." },
      { type: "p", text: "VantStride AI does not provide professional financial, investment, legal, tax, accounting or employment advice." },
      { type: "p", text: "Users should not submit passwords, payment-card information, government identification numbers, confidential employer information, proprietary documents or other highly sensitive information to VantStride AI." },
      { type: "p", text: "VantStride may modify, restrict, suspend or discontinue AI functionality as the platform evolves." },
    ],
  },
  {
    h: "7. Digital products",
    blocks: [
      { type: "p", text: "VantStride may offer paid or free digital products, including playbooks, ebooks, templates, guides, educational documents and other downloadable or accessible materials." },
      { type: "p", text: "Unless expressly stated otherwise, purchasing a digital product grants the purchaser a limited, personal, non-exclusive, non-transferable licence to use the product for their own individual educational purposes." },
      { type: "p", text: "Purchasing a product does not transfer ownership of the intellectual property contained in that product." },
      { type: "p", text: "Users may not reproduce, redistribute, resell, sublicense, upload, publicly share, commercially exploit or make VantStride digital products available to others without prior written permission." },
    ],
  },
  {
    h: "8. Payments and third-party checkout",
    blocks: [
      { type: "p", text: "VantStride may use third-party platforms or payment providers to process purchases and deliver digital products." },
      { type: "p", text: "When a user selects a purchase option and is redirected to an external checkout provider, the payment transaction may also be governed by that provider’s applicable terms and privacy policy." },
      { type: "p", text: "VantStride does not control the operation, availability or security of independent third-party payment platforms." },
      { type: "p", text: "Prices, currencies, discounts and availability may vary by product, market, promotional period or checkout platform." },
      { type: "p", text: "Any applicable taxes, payment processing requirements or currency conversion may depend on the user's location and the relevant payment provider." },
    ],
  },
  {
    h: "9. Refunds and digital goods",
    blocks: [
      { type: "p", text: "Refund eligibility for VantStride digital products is governed by the refund policy displayed by VantStride and/or the applicable checkout provider at the time of purchase, together with any mandatory rights available under applicable law." },
      { type: "p", text: "Because digital products may become accessible or downloadable shortly after purchase, users should review the product description carefully before completing a purchase." },
      { type: "p", text: "Nothing in these Terms excludes refund, cancellation or consumer rights that cannot lawfully be excluded." },
    ],
  },
  {
    h: "10. Third-party resources and affiliate links",
    blocks: [
      { type: "p", text: "VantStride curates and links to third-party courses, platforms, tools, communities, books, educational resources and other services." },
      { type: "p", text: "Some links may be affiliate links. This means VantStride may receive a commission or other benefit when a user purchases or signs up through certain links, potentially at no additional cost to the user." },
      { type: "p", text: "Affiliate relationships do not guarantee that a third-party product or service will be appropriate for every user." },
      { type: "p", text: "Third-party products and services are owned and operated independently. Their pricing, availability, content, terms, refund policies and performance are controlled by the relevant third party." },
      { type: "p", text: "VantStride is not responsible for the acts, omissions, availability or policies of independent third-party services." },
      { type: "p", text: "Users should review VantStride’s Affiliate Disclosure for additional information." },
    ],
  },
  {
    h: "11. Intellectual property",
    blocks: [
      { type: "p", text: "Unless otherwise stated, the VantStride brand, website content, original text, graphics, design elements, Career Hub materials, playbooks, ebooks, educational frameworks, downloadable resources and other original materials are owned by or licensed to VantStride and are protected by applicable intellectual-property laws." },
      { type: "p", text: "Users may use VantStride materials only for lawful personal use as permitted by these Terms." },
      { type: "p", text: "Without prior written permission, users may not:" },
      { type: "ul", items: [
        "copy or reproduce paid VantStride materials for distribution;",
        "resell or redistribute VantStride products;",
        "upload paid products to public or private sharing repositories;",
        "share purchased files with multiple unrelated users;",
        "remove proprietary notices or branding for the purpose of redistribution;",
        "represent VantStride content as their own;",
        "commercially exploit VantStride materials; or",
        "create unauthorized copies intended to substitute for the original product.",
      ]},
      { type: "p", text: "Nothing in these Terms limits rights that cannot legally be restricted under applicable law." },
    ],
  },
  {
    h: "12. Acceptable use",
    blocks: [
      { type: "p", text: "Users must not use VantStride to:" },
      { type: "ul", items: [
        "violate applicable laws;",
        "attempt unauthorized access to accounts, systems or data;",
        "interfere with platform security or normal operation;",
        "distribute malware or malicious code;",
        "scrape or systematically extract protected content in violation of applicable law or these Terms;",
        "use automated systems to abuse platform functionality;",
        "misuse VantStride AI;",
        "impersonate VantStride or another person;",
        "engage in fraud or deceptive activity; or",
        "infringe intellectual-property or other legal rights.",
      ]},
      { type: "p", text: "VantStride may take reasonable measures to protect the platform from misuse." },
    ],
  },
  {
    h: "13. Third-party content",
    blocks: [
      { type: "p", text: "References to third-party companies, employers, financial institutions, courses, platforms, tools or other organizations are provided for informational or recommendation purposes." },
      { type: "p", text: "Unless explicitly stated, such references do not imply sponsorship, endorsement, partnership or affiliation." },
      { type: "p", text: "Third-party information may change without notice. Users should verify important information directly with the relevant provider." },
    ],
  },
  {
    h: "14. Information accuracy",
    blocks: [
      { type: "p", text: "VantStride aims to provide useful and carefully prepared educational information, but finance careers, recruiting processes, compensation, industries, markets, courses and third-party resources can change." },
      { type: "p", text: "We do not warrant that every piece of information will always be complete, current, error-free or applicable to every user." },
      { type: "p", text: "VantStride may correct, update, remove or revise content when appropriate." },
    ],
  },
  {
    h: "15. Service availability",
    blocks: [
      { type: "p", text: "We aim to maintain reliable access to VantStride, but uninterrupted availability cannot be guaranteed." },
      { type: "p", text: "The Services may occasionally be unavailable because of maintenance, updates, hosting issues, third-party outages, technical problems, security concerns or circumstances outside our reasonable control." },
      { type: "p", text: "Features may be introduced, modified or discontinued as VantStride develops." },
    ],
  },
  {
    h: "16. Disclaimer of warranties",
    blocks: [
      { type: "p", text: "To the extent permitted by applicable law, VantStride and its Services are provided on an “as available” and “as is” basis." },
      { type: "p", text: "While we aim to provide useful and accurate resources, we do not make guarantees regarding uninterrupted availability, complete accuracy, specific career results or suitability for every user's individual circumstances." },
      { type: "p", text: "Nothing in this section excludes warranties or rights that cannot lawfully be excluded." },
    ],
  },
  {
    h: "17. Limitation of liability",
    blocks: [
      { type: "p", text: "To the maximum extent permitted by applicable law, VantStride will not be responsible for indirect, incidental, consequential or similar losses arising solely from reliance on educational information, third-party services, external resources, affiliate products or AI-generated content where such liability may lawfully be limited." },
      { type: "p", text: "Users remain responsible for their own career, educational, purchasing, financial and professional decisions." },
      { type: "p", text: "Nothing in these Terms excludes or limits liability where doing so would be prohibited by applicable law, including applicable mandatory consumer protections." },
    ],
  },
  {
    h: "18. Account suspension or termination",
    blocks: [
      { type: "p", text: "VantStride may restrict or suspend access where reasonably necessary to address fraud, security threats, unlawful activity, serious violations of these Terms, infringement of intellectual property or abuse of the platform." },
      { type: "p", text: "Where appropriate, VantStride may permanently terminate accounts involved in serious or repeated violations." },
      { type: "p", text: "Users may stop using VantStride at any time." },
    ],
  },
  {
    h: "19. Privacy",
    blocks: [
      { type: "p", text: "Use of VantStride is also subject to the VantStride Privacy Policy." },
      { type: "p", text: "The Privacy Policy explains how information may be collected, processed, retained and shared in connection with the Services." },
    ],
  },
  {
    h: "20. Changes to the Services",
    blocks: [
      { type: "p", text: "VantStride is an evolving platform." },
      { type: "p", text: "We may add, modify, replace or discontinue features, resources, Career Hubs, AI capabilities, digital products or other aspects of the Services." },
      { type: "p", text: "We may also update pricing for future purchases or introduce new free or paid features." },
      { type: "p", text: "Changes to future offerings do not automatically create an entitlement to features, products or pricing that were not included in a user's original purchase." },
    ],
  },
  {
    h: "21. Changes to these Terms",
    blocks: [
      { type: "p", text: "We may update these Terms as VantStride evolves or when operational, legal or regulatory requirements change." },
      { type: "p", text: "When material updates are made, the “Last updated” date will be revised." },
      { type: "p", text: "Continued use of the Services after updated Terms become effective constitutes acceptance to the extent permitted by applicable law. Where additional notice or consent is legally required, we will take appropriate steps." },
    ],
  },
  {
    h: "22. Severability",
    blocks: [
      { type: "p", text: "If any provision of these Terms is found to be invalid, unlawful or unenforceable, the remaining provisions will continue to apply to the extent permitted by law." },
    ],
  },
  {
    h: "23. Entire agreement",
    blocks: [
      { type: "p", text: "These Terms, together with the Privacy Policy, Affiliate Disclosure, applicable Refund Policy and any product-specific terms presented at purchase, constitute the applicable terms governing use of VantStride, subject to rights that cannot be excluded under applicable law." },
    ],
  },
  {
    h: "24. Contact",
    blocks: [
      { type: "p", text: "Questions regarding these Terms may be sent to:" },
      { type: "p", text: "VantStride" },
      { type: "mailto", email: "support@vantstride.com" },
    ],
  },
];

const AFFILIATE_SECTIONS = [
  {
    h: "1. Our approach to affiliate links",
    blocks: [
      { type: "p", text: "VantStride provides finance-career education, Career Hubs, ebooks, playbooks, curated learning resources, tools and recommendations." },
      { type: "p", text: "Some links available through VantStride are affiliate links. This means VantStride may earn a commission, referral fee or other compensation if you click an eligible link and subsequently make a purchase, subscribe to a service or complete another qualifying action." },
      { type: "p", text: "Where applicable, this compensation generally comes from the third-party provider and does not increase the price you pay solely because you accessed the provider through a VantStride affiliate link." },
    ],
  },
  {
    h: "2. Why we use affiliate links",
    blocks: [
      { type: "p", text: "Affiliate partnerships are one of the ways VantStride may generate revenue to support the development, operation and maintenance of the platform." },
      { type: "p", text: "This can help support Career Hubs, educational content, VantStride AI, platform infrastructure and the continued development of resources for users." },
      { type: "p", text: "The existence of an affiliate relationship does not guarantee that a particular product, course, platform or service is suitable for every user." },
    ],
  },
  {
    h: "3. Our recommendation principles",
    blocks: [
      { type: "p", text: "VantStride aims to feature resources that we believe may be relevant or useful to people preparing for finance careers." },
      { type: "p", text: "Affiliate compensation does not guarantee positive coverage, a particular ranking, or inclusion on VantStride." },
      { type: "p", text: "Recommendations are based on factors we consider relevant to users, which may include the subject matter, educational value, usefulness, relevance and positioning of the resource." },
      { type: "p", text: "However, recommendations are inherently judgment-based, and users should independently determine whether a particular resource is appropriate for their own needs." },
    ],
  },
  {
    h: "4. Identifying affiliate links",
    blocks: [
      { type: "p", text: "Where reasonably practicable, VantStride will disclose or identify affiliate relationships in proximity to recommendations, links or relevant sections of the platform." },
      { type: "p", text: "Labels may include wording such as:" },
      { type: "ul", items: [
        "“Affiliate link”",
        "“Affiliate partner”",
        "“Partner”",
        "“VantStride may earn a commission”",
      ]},
      { type: "p", text: "or similar clear language." },
      { type: "p", text: "The absence of a specific label next to a particular third-party link does not necessarily mean that no commercial or affiliate relationship exists. This Affiliate Disclosure applies generally to affiliate links appearing throughout VantStride." },
    ],
  },
  {
    h: "5. Third-party products and services",
    blocks: [
      { type: "p", text: "Products, courses, subscriptions, software, educational programs, platforms and other services accessed through affiliate links are provided by independent third parties." },
      { type: "p", text: "VantStride does not own or control those third-party products or services unless explicitly stated otherwise." },
      { type: "p", text: "Third-party providers are responsible for their own:" },
      { type: "ul", items: [
        "products and services;",
        "pricing;",
        "availability;",
        "course or product content;",
        "subscriptions;",
        "billing;",
        "refund and cancellation policies;",
        "customer support;",
        "privacy practices; and",
        "terms and conditions.",
      ]},
      { type: "p", text: "Before purchasing or subscribing, users should review the relevant provider's current information, terms and policies." },
    ],
  },
  {
    h: "6. Prices, offers and information may change",
    blocks: [
      { type: "p", text: "Prices, discounts, promotions, product features, course availability, subscription terms and other information provided by third parties may change without notice." },
      { type: "p", text: "VantStride may attempt to keep recommendations and information useful and current, but we cannot guarantee that third-party information displayed or referenced on the platform will always remain complete, accurate or up to date." },
      { type: "p", text: "Users should verify important purchase information directly on the third-party provider's website before completing a transaction." },
    ],
  },
  {
    h: "7. No guarantee of results",
    blocks: [
      { type: "p", text: "The inclusion or recommendation of a third-party course, platform, tool, resource or service does not constitute a guarantee of results." },
      { type: "p", text: "Purchasing or using a recommended resource does not guarantee employment, internships, interviews, promotions, compensation, admission, certifications, investment results or any particular career outcome." },
      { type: "p", text: "Individual results depend on numerous factors outside VantStride's control." },
    ],
  },
  {
    h: "8. Editorial independence",
    blocks: [
      { type: "p", text: "VantStride aims to keep its educational content and recommendations useful to its audience." },
      { type: "p", text: "An affiliate relationship does not mean that a third-party provider controls VantStride's editorial content." },
      { type: "p", text: "Where VantStride receives affiliate compensation, the commercial relationship does not give the third party authority to determine VantStride's independent educational opinions or conclusions." },
    ],
  },
  {
    h: "9. Not all links are affiliate links",
    blocks: [
      { type: "p", text: "Not every external link or recommendation appearing on VantStride is necessarily an affiliate link." },
      { type: "p", text: "VantStride may also link to free resources, informational websites, educational materials, communities, tools or other third-party resources without receiving compensation." },
    ],
  },
  {
    h: "10. No additional endorsement or partnership",
    blocks: [
      { type: "p", text: "The presence of a third-party product, service, brand or resource on VantStride does not by itself mean that the third party sponsors, endorses or is formally partnered with VantStride." },
      { type: "p", text: "Where an actual commercial, affiliate or other relationship exists, it should be understood only to the extent of that specific relationship." },
      { type: "p", text: "Third-party names, logos and trademarks belong to their respective owners." },
    ],
  },
  {
    h: "11. Your purchasing decisions",
    blocks: [
      { type: "p", text: "Users are responsible for evaluating products and services before purchasing them." },
      { type: "p", text: "We encourage users to consider their own goals, budget, experience level and needs and to review current product details directly from the provider." },
      { type: "p", text: "You are never required to purchase a third-party product or service in order to use the generally available portions of VantStride unless a particular feature or offering expressly states otherwise." },
    ],
  },
  {
    h: "12. Updates to this disclosure",
    blocks: [
      { type: "p", text: "VantStride may add, remove or change affiliate relationships over time." },
      { type: "p", text: "Accordingly, this Affiliate Disclosure may be updated periodically to reflect changes in our platform, business model, applicable requirements or disclosure practices." },
      { type: "p", text: "The date at the top of this page indicates when this disclosure was last updated." },
    ],
  },
  {
    h: "13. Contact",
    blocks: [
      { type: "p", text: "If you have questions about an affiliate relationship, recommendation or this Affiliate Disclosure, please contact:" },
      { type: "p", text: "VantStride" },
      { type: "mailto", email: "support@vantstride.com" },
    ],
  },
];

const REFUND_SECTIONS = [
  {
    h: "1. No-Refund Policy",
    blocks: [
      { type: "p", text: "VantStride does not provide refunds, exchanges, or credits for digital products due to:" },
      { type: "ul", items: [
        "change of mind;",
        "accidental purchase;",
        "purchasing the wrong product;",
        "purchasing a product that is no longer needed;",
        "deciding that the product is not suitable for your personal preferences;",
        "failure to use the product;",
        "failure to complete or read the product;",
        "dissatisfaction with the product where the product is materially consistent with its published description;",
        "failure to achieve a particular academic, educational, career, employment, financial, or other personal or professional outcome;",
        "differences in individual learning preferences or expectations;",
        "failure to review the product description or available preview material before purchase;",
        "purchasing a product because of an expectation of a particular result that VantStride did not expressly guarantee;",
        "incompatibility with a device, software, application, operating system, or other technical environment where the relevant product requirements or format were reasonably disclosed before purchase; or",
        "failure to use the product within a particular period of time.",
      ]},
      { type: "p", text: "Once access to a digital product has been provided or the product has been made available for download, the purchase is considered final." },
    ],
  },
  {
    h: "2. Product Descriptions and Pre-Purchase Review",
    blocks: [
      { type: "p", text: "VantStride makes reasonable efforts to accurately describe its products, including their contents, features, file formats, included resources, scope, and intended use." },
      { type: "p", text: "Customers are encouraged to review the relevant product page, description, previews, sample material, included-resource list, and other available information before purchasing." },
      { type: "p", text: "If you have questions about a product before purchasing, you are encouraged to contact VantStride before completing the purchase." },
    ],
  },
  {
    h: "3. Technical Access Problems",
    blocks: [
      { type: "p", text: "VantStride does not provide refunds merely because a customer experiences a temporary technical difficulty accessing or downloading a product." },
      { type: "p", text: "If you experience a genuine technical problem that prevents you from accessing a product you purchased, please contact VantStride at:" },
      { type: "mailto", email: "support@vantstride.com", plain: true },
      { type: "p", text: "VantStride will make reasonable efforts to resolve verified access or delivery problems, which may include:" },
      { type: "ul", items: [
        "restoring access;",
        "providing another download opportunity;",
        "providing a replacement file;",
        "correcting a defective or corrupted file where applicable; or",
        "providing reasonable technical assistance.",
      ]},
      { type: "p", text: "Where the issue can reasonably be resolved by restoring access or providing a functioning replacement, VantStride may do so without issuing a refund." },
    ],
  },
  {
    h: "4. Duplicate Transactions",
    blocks: [
      { type: "p", text: "If you believe you have been charged more than once for the same transaction, please contact VantStride with the relevant transaction information." },
      { type: "p", text: "VantStride will review the transaction and take appropriate corrective action where a genuine duplicate charge is verified." },
    ],
  },
  {
    h: "5. Product Updates and Changes",
    blocks: [
      { type: "p", text: "VantStride may update, improve, expand, revise, replace, or discontinue aspects of its digital products from time to time." },
      { type: "p", text: "Changes to a product after purchase do not automatically create a right to a refund." },
      { type: "p", text: "Where applicable, customers may receive updated versions or revised files at VantStride's discretion or as specified on the relevant product page." },
    ],
  },
  {
    h: "6. Refunds and Product Access",
    blocks: [
      { type: "p", text: "If a refund is provided or required under applicable law or the applicable payment platform's rules, the purchaser's right to use the refunded product may be terminated or restricted." },
      { type: "p", text: "Where access is revoked following a refund, the purchaser must discontinue use of the refunded product and delete or cease using copies of the product to the extent permitted by applicable law." },
      { type: "p", text: "A refund does not grant the purchaser any continuing right to reproduce, distribute, resell, share, publish, upload, or otherwise use the product in violation of VantStride's Terms & Conditions or Copyright & Intellectual Property Policy." },
    ],
  },
  {
    h: "7. Payment Platforms and Chargebacks",
    blocks: [
      { type: "p", text: "VantStride may use third-party payment and digital-product platforms, including Gumroad and Payhip, to process transactions and deliver products." },
      { type: "p", text: "Those platforms may have their own payment, dispute, chargeback, refund, and consumer-protection procedures." },
      { type: "p", text: "VantStride's no-refund policy does not prevent a payment platform, card issuer, payment processor, or other authorized entity from applying its own rules or obligations." },
      { type: "p", text: "If a customer has a concern regarding a purchase, VantStride encourages the customer to contact support@vantstride.com before initiating a chargeback or payment dispute so that the issue can be reviewed and, where possible, resolved." },
      { type: "p", text: "Gumroad states that creators can establish their own refund policies, while Gumroad may handle certain refund, chargeback, or dispute matters under its platform terms. Payhip likewise states that sellers establish and manage their own refund policies." },
    ],
  },
  {
    h: "8. Consumer Rights",
    blocks: [
      { type: "p", text: "Nothing in this Refund Policy is intended to exclude, restrict, or waive any consumer right, remedy, cancellation right, refund right, or other legal protection that cannot lawfully be excluded or waived under applicable law." },
      { type: "p", text: "Where applicable law requires a refund, cancellation, replacement, or other remedy, VantStride will comply with the applicable legal requirement." },
    ],
  },
  {
    h: "9. Policy Changes",
    blocks: [
      { type: "p", text: "VantStride may update this Refund Policy from time to time to reflect changes to its products, business practices, payment providers, applicable laws, or regulatory requirements." },
      { type: "p", text: "The updated version will be published on this page with a revised effective date." },
    ],
  },
  {
    h: "10. Contact",
    blocks: [
      { type: "p", text: "For questions regarding a purchase, product access, or a technical issue, please contact:" },
      { type: "p", text: "VantStride" },
      { type: "mailto", email: "support@vantstride.com" },
      { type: "p", text: "When contacting VantStride regarding a purchase, please provide:" },
      { type: "ul", items: [
        "the email address used for the purchase;",
        "the product name;",
        "the approximate purchase date;",
        "your order or transaction information, where available; and",
        "a description of the issue.",
      ]},
    ],
  },
];

const COPYRIGHT_IP_SECTIONS = [
  {
    h: "1. Ownership",
    blocks: [
      { type: "p", text: "Unless otherwise stated, the VantStride website, brand, logo, original written content, graphics, designs, Career Hubs, educational frameworks, playbooks, ebooks, templates, spreadsheets, workbooks, guides, downloadable resources, AI-related materials, and other original materials created or provided by VantStride are owned by or licensed to VantStride and are protected by applicable intellectual-property laws." },
      { type: "p", text: "VantStride retains all rights not expressly granted to users under the applicable Terms & Conditions or a specific written licence." },
    ],
  },
  {
    h: "2. Personal Licence for Digital Products",
    blocks: [
      { type: "p", text: "When you purchase or receive access to a VantStride digital product, you receive a limited, personal, non-exclusive, non-transferable licence to use that product for your own individual educational, professional-development, and career-preparation purposes." },
      { type: "p", text: "Your purchase does not transfer ownership of the underlying intellectual property." },
    ],
  },
  {
    h: "3. Permitted Personal Use",
    blocks: [
      { type: "p", text: "You may use VantStride digital products for your own personal educational, professional-development, and career-preparation purposes." },
      { type: "p", text: "Where a VantStride product contains templates, trackers, workbooks, or other materials intended to be completed or adapted by the purchaser, you may edit and customise those materials for your own individual use." },
      { type: "p", text: "You may also use the resulting personal work product, including appropriately customised CVs, resumes, cover letters, networking messages, outreach messages, interview preparation materials, notes, analyses, and similar materials, for your own career, educational, or professional purposes." },
      { type: "p", text: "This permission does not grant you the right to redistribute, resell, publish, or commercially distribute the underlying VantStride templates, files, content, or resources." },
    ],
  },
  {
    h: "4. Prohibited Use",
    blocks: [
      { type: "p", text: "Unless VantStride has given prior written permission, you may not:" },
      { type: "ul", items: [
        "copy or reproduce a VantStride digital product for distribution to others;",
        "share purchased files with other people;",
        "upload VantStride products to public or private file-sharing services;",
        "upload products to Google Drive, Dropbox, Telegram, Discord, Reddit, forums, repositories, websites, marketplaces, or other locations for access by others;",
        "resell, sublicense, rent, lease, distribute, or commercially exploit VantStride products;",
        "publish, repost, or distribute substantial portions of VantStride content;",
        "remove or deliberately obscure VantStride copyright, licensing, ownership, or branding notices for redistribution;",
        "represent VantStride materials as your own;",
        "create or distribute unauthorized copies, modified versions, or derivative versions intended to substitute for the original product; or",
        "use VantStride materials to create or operate a competing product or resource library without written permission.",
      ]},
    ],
  },
  {
    h: "5. Buyer-Specific Protection",
    blocks: [
      { type: "p", text: "Certain VantStride digital products may be distributed through third-party commerce platforms that apply purchaser-specific protections, including email stamping, purchase information, download controls, access controls, or other technical measures." },
      { type: "p", text: "Where such measures are used, removing, altering, obscuring, or bypassing those protections for the purpose of unauthorized distribution is prohibited." },
    ],
  },
  {
    h: "6. Unauthorized Distribution",
    blocks: [
      { type: "p", text: "If VantStride discovers that its products or copyrighted materials have been distributed, uploaded, reproduced, resold, or otherwise made available without authorization, VantStride may take appropriate action, which may include:" },
      { type: "ul", items: [
        "requesting removal of the infringing material;",
        "submitting copyright or intellectual-property infringement notices to the relevant platform, website, marketplace, host, search engine, or service provider;",
        "restricting or terminating access to VantStride accounts or products where permitted;",
        "investigating the source of an unauthorized copy;",
        "contacting the relevant purchaser or distributor;",
        "seeking recovery of losses or other remedies available under applicable law; and",
        "pursuing legal remedies where appropriate.",
      ]},
      { type: "p", text: "Nothing in this section limits any rights or remedies available to VantStride under applicable law." },
    ],
  },
  {
    h: "7. Reporting Suspected Infringement",
    blocks: [
      { type: "p", text: "If you believe that VantStride content has been copied, uploaded, distributed, resold, or otherwise used without authorization, please contact us." },
      { type: "p", text: "Please provide:" },
      { type: "ul", items: [
        "the URL or location where the suspected infringement appears;",
        "the name of the VantStride product or material involved;",
        "a description of the material that appears to have been copied;",
        "screenshots or other evidence, where available;",
        "the date you discovered the suspected infringement; and",
        "any information that may help us identify the source.",
      ]},
      { type: "p", text: "Copyright & IP Contact:" },
      { type: "p", text: "VantStride" },
      { type: "mailto", email: "support@vantstride.com" },
      { type: "p", text: "We may request additional information reasonably necessary to verify ownership and investigate a report." },
    ],
  },
  {
    h: "8. Third-Party Material",
    blocks: [
      { type: "p", text: "Some VantStride products may contain or reference third-party trademarks, data, quotations, publicly available information, or other materials belonging to their respective owners." },
      { type: "p", text: "Such material remains subject to the rights of the relevant third party. Nothing on this page transfers ownership of third-party intellectual property to VantStride or to users." },
      { type: "p", text: "VantStride is not affiliated with, endorsed by, sponsored by, or otherwise officially connected with any third-party company, financial institution, fund, employer, educational provider, or other organisation referenced in its products, unless expressly stated otherwise." },
      { type: "p", text: "Third-party names, trademarks, logos, company information, transactions, case studies, and other references are used for educational, informational, analytical, or illustrative purposes and remain the property of their respective owners." },
    ],
  },
  {
    h: "9. Copyright Notices",
    blocks: [
      { type: "p", text: "Copyright notices appearing on VantStride materials are intended to identify ownership and applicable usage restrictions." },
      { type: "p", text: "The absence of a copyright notice does not necessarily mean that material is free to copy or distribute." },
    ],
  },
  {
    h: "10. Reviews, Quotes, and Personal Use of Content",
    blocks: [
      { type: "p", text: "Users may refer to VantStride products and may use brief quotations or excerpts for genuine personal reviews, commentary, or recommendations, provided that such use does not reproduce substantial portions of the product and does not imply ownership, affiliation, or endorsement." },
      { type: "p", text: "Where appropriate, users should identify VantStride as the source." },
    ],
  },
  {
    h: "11. Group and Institutional Licences",
    blocks: [
      { type: "p", text: "VantStride may offer separate licences or permissions for universities, educational institutions, finance societies, teams, employers, organisations, or other groups requiring access for multiple users." },
      { type: "p", text: "If you require multi-user or institutional access, please contact VantStride before purchasing or distributing a product intended for individual use." },
    ],
  },
  {
    h: "12. Career Finance AI and User-Generated Outputs",
    blocks: [
      { type: "p", text: "Where VantStride provides AI-powered tools or features that generate responses, suggestions, drafts, analyses, or other outputs for a user, the user's use of those outputs is subject to the applicable Terms & Conditions and any specific terms presented with the relevant AI feature." },
      { type: "p", text: "VantStride's underlying software, systems, prompts, frameworks, proprietary content, databases, methodologies, interfaces, and other intellectual property remain protected and are not transferred to the user merely because an output is generated." },
    ],
  },
  {
    h: "13. Enforcement",
    blocks: [
      { type: "p", text: "VantStride reserves the right to protect its intellectual property using available contractual, platform-based, administrative, and legal remedies." },
      { type: "p", text: "VantStride does not waive any rights by permitting users to access or purchase its materials." },
    ],
  },
];

const CONTENT = {
  "copyright-ip": {
    title: "Copyright & Intellectual Property",
    label: "Legal",
    last_updated: "September 2026",
    sections: COPYRIGHT_IP_SECTIONS,
  },
  "refund-policy": {
    title: "Refund Policy",
    label: "Legal",
    date_label: "Effective Date",
    last_updated: "18 September 2026",
    intro: [
      "VantStride provides digital products and resources, including playbooks, ebooks, guides, templates, spreadsheets, workbooks, trackers, career resources, downloadable files, educational materials, and other digitally delivered products.",
      "Because VantStride products are digital products that may be accessed, downloaded, copied, or stored immediately after purchase, all purchases are final and VantStride does not offer refunds for digital products once access to the purchased product has been provided or the product has been made available for download.",
      "Please review the product description, previews, included resources, features, format, and other available information carefully before purchasing.",
    ],
    sections: REFUND_SECTIONS,
  },
  privacy: {
    title: "Privacy Policy",
    label: "Legal",
    last_updated: "August 2026",
    sections: PRIVACY_SECTIONS,
  },
  terms: {
    title: "Terms & Conditions",
    label: "Legal",
    last_updated: "August 2026",
    sections: TERMS_SECTIONS,
  },
  "affiliate-disclosure": {
    title: "Affiliate Disclosure",
    label: "Transparency",
    last_updated: "August 2026",
    sections: AFFILIATE_SECTIONS,
  },
};

function SectionBody({ section }) {
  // Backwards compatible: `p` (single string) still works for terms and affiliate-disclosure.
  if (section.p) {
    return <p className="mt-2 text-slate-600 leading-relaxed">{section.p}</p>;
  }
  return (
    <div className="mt-2 space-y-4">
      {section.blocks.map((b, i) => {
        if (b.type === "h3") {
          return (
            <h3
              key={i}
              className="font-display text-base text-[#0A2540] font-medium mt-2"
            >
              {b.text}
            </h3>
          );
        }
        if (b.type === "ul") {
          return (
            <ul
              key={i}
              className="list-disc pl-5 text-slate-600 leading-relaxed space-y-1.5 marker:text-slate-400"
            >
              {b.items.map((it, j) => (
                <li key={j}>{it}</li>
              ))}
            </ul>
          );
        }
        if (b.type === "mailto") {
          return (
            <p key={i} className="text-slate-600 leading-relaxed">
              {b.plain ? null : <>Email:{" "}</>}
              <a
                href={`mailto:${b.email}`}
                className="text-[#0A2540] underline underline-offset-2 hover:text-[#051A2E]"
                data-testid="legal-support-email"
              >
                {b.email}
              </a>
            </p>
          );
        }
        return (
          <p key={i} className="text-slate-600 leading-relaxed">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

export default function Legal({ slug }) {
  const data = CONTENT[slug];
  if (!data) return null;

  const lastUpdated =
    data.last_updated ||
    new Date().toLocaleDateString(undefined, { year: "numeric", month: "long" });
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A2540]"
        data-testid="legal-back-home"
      >
        <ArrowLeft className="w-4 h-4" /> Home
      </Link>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500">{data.label}</div>
        <h1
          className="font-display text-4xl sm:text-5xl tracking-tight text-[#0A2540] font-medium mt-2"
          data-testid={`legal-title-${slug}`}
        >
          {data.title}
        </h1>
        <p className="mt-3 text-sm text-slate-500" data-testid={`legal-last-updated-${slug}`}>
          {data.date_label || "Last updated"}: {lastUpdated}.
        </p>
      </div>

      {data.intro && (
        <div className="mt-10 space-y-4">
          {data.intro.map((t, i) => (
            <p key={i} className="text-slate-600 leading-relaxed">{t}</p>
          ))}
        </div>
      )}

      <div className="mt-10 space-y-8">
        {data.sections.map((s) => (
          <section key={s.h}>
            <h2 className="font-display text-xl text-[#0A2540] font-medium">{s.h}</h2>
            <SectionBody section={s} />
          </section>
        ))}
      </div>

      <p className="mt-12 text-xs text-slate-400">
        This page is provided for informational purposes only and does not constitute legal advice.
      </p>
    </div>
  );
}
